import type { ReactNode } from "react";

export type DataTableAlign = "start" | "center" | "end";

export type DataTableColumn<T> = {
  /** Stable id for the column. Used for the React key on header/cell. */
  id: string;
  /** Header label or custom node. */
  header: ReactNode;
  /** Cell renderer. */
  cell: (row: T, index: number) => ReactNode;
  /**
   * Column width for the `<colgroup>`. Accepts any CSS length (e.g. "120px",
   * "10rem", "minmax(0,1fr)"). Omit to let the column flex to fill leftover
   * space in `table-fixed` mode (one column should typically be flex).
   */
  width?: string | number;
  /** Horizontal alignment for both header and cell — defaults to "start". */
  align?: DataTableAlign;
  /** Append extra classes to the `<th>` element. */
  headerClassName?: string;
  /** Append extra classes to the `<td>` element. */
  cellClassName?: string;
};

export type DataTableProps<T> = {
  rows: T[];
  columns: DataTableColumn<T>[];
  /** Stable id per row — required for React key correctness. */
  rowKey: (row: T) => string;
  /** Rendered when `rows.length === 0`. */
  empty?: ReactNode;
  /** Class merged onto the outer `<table>`. */
  className?: string;
  /**
   * When true (default), uses `table-fixed` layout so columns honor the
   * widths in `<colgroup>` exactly. Set false to let the browser auto-size.
   */
  fixed?: boolean;
  /**
   * Optional row-level handler. If provided, rows render with a hover state
   * and `cursor: pointer`.
   */
  onRowClick?: (row: T, index: number) => void;
  /**
   * Optional per-row class hook (e.g. tint a row that needs attention). The
   * returned classes are merged onto the `<tr>`.
   */
  rowClassName?: (row: T, index: number) => string | undefined;
  /** Aria label for the table — useful when there's no visible caption. */
  ariaLabel?: string;
};

const ALIGN_CLASS: Record<DataTableAlign, string> = {
  start: "text-start",
  center: "text-center",
  end: "text-end",
};

const TH_BASE =
  "border-b border-[color:var(--border-light)] bg-[color:var(--background-muted)] px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-[color:var(--text-muted)]";

const TD_BASE =
  "border-b border-[color:var(--border-light)] px-3 py-2.5 align-middle text-[12.5px]";

/**
 * Reusable data table primitive used across the seller and admin portals.
 * Renders a real `<table>` with a `<colgroup>` so header/body columns are
 * guaranteed to share the same widths. Cell contents come from `columns[].cell`,
 * keeping callers free of layout concerns.
 */
export function DataTable<T>({
  rows,
  columns,
  rowKey,
  empty,
  className,
  fixed = true,
  onRowClick,
  rowClassName,
  ariaLabel,
}: DataTableProps<T>) {
  if (rows.length === 0 && empty !== undefined) {
    return <>{empty}</>;
  }

  const tableClass = [
    "w-full border-collapse",
    fixed ? "table-fixed" : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="w-full overflow-x-auto">
      <table className={tableClass} aria-label={ariaLabel}>
        <colgroup>
          {columns.map((col) => (
            <col
              key={col.id}
              style={
                col.width !== undefined
                  ? {
                      width:
                        typeof col.width === "number"
                          ? `${col.width}px`
                          : col.width,
                    }
                  : undefined
              }
            />
          ))}
        </colgroup>
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.id}
                className={[
                  TH_BASE,
                  ALIGN_CLASS[col.align ?? "start"],
                  col.headerClassName ?? "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                scope="col"
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => {
            const interactive = Boolean(onRowClick);
            return (
              <tr
                key={rowKey(row)}
                onClick={
                  interactive ? () => onRowClick?.(row, rowIndex) : undefined
                }
                className={
                  [
                    interactive
                      ? "cursor-pointer transition-colors hover:bg-[color:var(--background-muted)]"
                      : "",
                    rowClassName?.(row, rowIndex) ?? "",
                  ]
                    .filter(Boolean)
                    .join(" ") || undefined
                }
              >
                {columns.map((col) => (
                  <td
                    key={col.id}
                    className={[
                      TD_BASE,
                      ALIGN_CLASS[col.align ?? "start"],
                      col.cellClassName ?? "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {col.cell(row, rowIndex)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
